import React, { useState } from 'react';
import { ChevronRight, Check, LockKeyhole } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
interface CalendarTabProps {
  theme: ZodiacTheme;
}
export function CalendarTab({
  theme
}: CalendarTabProps) {
  const [hasProCalendar, setHasProCalendar] = useState(false);
  const {
    success
  } = useToast();
  return <div className="space-y-6" data-id="element-17541">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-17542">
        <div data-id="element-17543">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-17544">
            Персональный календарь
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-17545">
            Транзиты и рекомендации на основе вашей натальной карты
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm" data-id="element-17546">
          <span className="text-sm font-medium text-slate-700" data-id="element-17547">
            Октябрь 2024
          </span>
          <div className="flex gap-1 ml-2" data-id="element-17548">
            <button className="p-1 hover:bg-slate-100 rounded" data-id="element-17549">
              <ChevronRight className="w-4 h-4 rotate-180" data-id="element-17550" />
            </button>
            <button className="p-1 hover:bg-slate-100 rounded" data-id="element-17551">
              <ChevronRight className="w-4 h-4" data-id="element-17552" />
            </button>
          </div>
        </div>
      </div>

      {!hasProCalendar ? <Card padding="lg" className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white relative overflow-hidden" data-id="element-17553">
          <div className="absolute right-0 top-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl" data-id="element-17554"></div>
          <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center" data-id="element-17555">
            <div className="flex-1" data-id="element-17556">
              <Badge variant="pro" className="mb-4" data-id="element-17557">
                PRO Календарь
              </Badge>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-17558">
                Откройте персональные транзиты на каждый день
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-17559">
                Базовый календарь показывает только общие лунные фазы.
                Подключите Astriqo Plus, чтобы видеть, как планеты влияют лично
                на вас, с ежедневными рекомендациями.
              </p>
              <ul className="space-y-3 mb-6" data-id="element-17560">
                <li className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-17561">
                  <Check className="w-4 h-4 text-emerald-500" data-id="element-17562" /> Индивидуальные
                  благоприятные дни
                </li>
                <li className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-17563">
                  <Check className="w-4 h-4 text-emerald-500" data-id="element-17564" /> Предупреждения
                  о напряженных аспектах
                </li>
                <li className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-17565">
                  <Check className="w-4 h-4 text-emerald-500" data-id="element-17566" /> Синхронизация с
                  Google Calendar
                </li>
              </ul>
              <Button variant="primary" onClick={() => {
            setHasProCalendar(true);
            success('PRO Календарь активирован!');
          }} data-id="element-17567">
                Подключить за 990 ₽/мес
              </Button>
            </div>
            <div className="w-full md:w-1/3 relative" data-id="element-17568">
              <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10" data-id="element-17569"></div>
              <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-4 opacity-50 blur-[2px]" data-id="element-17570">
                <div className="h-8 bg-slate-100 rounded mb-3" data-id="element-17571"></div>
                <div className="space-y-2" data-id="element-17572">
                  <div className="h-12 bg-indigo-50 rounded" data-id="element-17573"></div>
                  <div className="h-12 bg-rose-50 rounded" data-id="element-17574"></div>
                  <div className="h-12 bg-emerald-50 rounded" data-id="element-17575"></div>
                </div>
              </div>
              <div className="absolute inset-0 z-20 flex items-center justify-center" data-id="element-17576">
                <div className="bg-white rounded-full p-4 shadow-xl" data-id="element-17577">
                  <LockKeyhole className="w-8 h-8 text-indigo-500" data-id="element-17578" />
                </div>
              </div>
            </div>
          </div>
        </Card> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-17579">
          <div className="lg:col-span-2" data-id="element-17580">
            <Card padding="none" className="overflow-hidden" data-id="element-17581">
              <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50" data-id="element-17582">
                {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => <div key={day} className="py-3 text-center text-xs font-bold text-slate-500 uppercase" data-id="element-17583">
                    {day}
                  </div>)}
              </div>
              <div className="grid grid-cols-7 grid-rows-5 bg-slate-200 gap-px" data-id="element-17584">
                {Array.from({
              length: 35
            }).map((_, i) => {
              const day = i - 1; // Offset for starting day
              const isCurrentMonth = day > 0 && day <= 31;
              const isToday = day === 15;
              const hasGoodAspect = day === 12 || day === 18 || day === 25;
              const hasBadAspect = day === 14 || day === 22;
              return <div key={i} className={`min-h-[100px] p-2 ${isCurrentMonth ? 'bg-white' : 'bg-slate-50 text-slate-400'} ${isToday ? `ring-2 inset-0 z-10 ${theme.ring}` : ''} relative group cursor-pointer hover:bg-slate-50 transition-colors`} data-id="element-17585">
                      <span className={`text-sm font-medium ${isToday ? theme.text : ''}`} data-id="element-17586">
                        {isCurrentMonth ? day : ''}
                      </span>

                      {isCurrentMonth && <div className="mt-2 space-y-1" data-id="element-17587">
                          {hasGoodAspect && <div className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded truncate" data-id="element-17588">
                              Трин Венера
                            </div>}
                          {hasBadAspect && <div className="text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded truncate" data-id="element-17589">
                              Квадрат Марс
                            </div>}
                          {day === 10 && <div className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded truncate" data-id="element-17590">
                              🌑 Новолуние
                            </div>}
                        </div>}
                    </div>;
            })}
              </div>
            </Card>
          </div>
          <div className="lg:col-span-1 space-y-4" data-id="element-17591">
            <Card padding="md" className={`border-t-4 ${theme.border.replace('border-', 'border-t-')}`} data-id="element-17592">
              <h3 className="font-bold text-slate-900 mb-4" data-id="element-17593">
                Прогноз на сегодня (15 Окт)
              </h3>
              <div className="space-y-4" data-id="element-17594">
                <div className="flex gap-3" data-id="element-17595">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 text-emerald-600" data-id="element-17596">
                    ↑
                  </div>
                  <div data-id="element-17597">
                    <div className="text-sm font-bold text-slate-900" data-id="element-17598">
                      Финансы и карьера
                    </div>
                    <div className="text-xs text-slate-600 mt-1" data-id="element-17599">
                      Отличный день для переговоров. Меркурий в секстиле к
                      вашему натальному Юпитеру.
                    </div>
                  </div>
                </div>
                <div className="flex gap-3" data-id="element-17600">
                  <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0 text-rose-600" data-id="element-17601">
                    ↓
                  </div>
                  <div data-id="element-17602">
                    <div className="text-sm font-bold text-slate-900" data-id="element-17603">
                      Отношения
                    </div>
                    <div className="text-xs text-slate-600 mt-1" data-id="element-17604">
                      Возможны недопонимания вечером. Постарайтесь не принимать
                      важных решений.
                    </div>
                  </div>
                </div>
              </div>
              <Button variant="outline" fullWidth className="mt-6" data-id="element-17605">
                Подробный прогноз
              </Button>
            </Card>
          </div>
        </div>}
    </div>;
}